import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, Instagram } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { postContact } from "@/lib/api";
import { TID } from "@/constants/testIds";

const TYPES = [
  { id: "general", label: "General inquiry" },
  { id: "gallery", label: "Gallery representation" },
  { id: "media", label: "Press / media request" },
  { id: "purchase", label: "Acquisition / purchase" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    inquiry_type: "general",
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await postContact(form);
      toast.success("Message sent. Colin will respond personally.");
      setForm({ name: "", email: "", subject: "", message: "", inquiry_type: form.inquiry_type });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contact Colin Darke Studio"
        description="Contact Colin Darke — for gallery representation, press requests, acquisitions or general inquiries."
        path="/contact"
      />

      <section className="pt-16 pb-6 border-b border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
          <p className="catalog-label mb-4">Contact</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
            The studio is
            <br />
            <span className="italic text-[hsl(var(--vermillion))]">
              always listening.
            </span>
          </h1>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-4 space-y-8">
            <div className="flex items-start gap-3">
              <MapPin size={18} className="mt-1 text-[hsl(var(--vermillion))]" />
              <div>
                <p className="catalog-label">Studio</p>
                <p className="mt-1">Detroit, Michigan · USA</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail size={18} className="mt-1 text-[hsl(var(--vermillion))]" />
              <div>
                <p className="catalog-label">Studio inbox</p>
                <p className="mt-1">Sent via the form — replies come from the studio.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Instagram size={18} className="mt-1 text-[hsl(var(--vermillion))]" />
              <div>
                <p className="catalog-label">Follow</p>
                <p className="mt-1">Instagram · TikTok · YouTube</p>
              </div>
            </div>

            <div className="mt-8 p-6 border border-[hsl(var(--line))] bg-[hsl(var(--surface-elevated))]">
              <p className="catalog-label mb-2">Commissions</p>
              <p className="text-sm">
                Looking for a custom ambidextrous portrait? Use the dedicated
                commission form for medium, size, and reference-photo flow.
              </p>
              <a
                href="/commission"
                className="mt-3 inline-block font-mono text-xs uppercase tracking-widest text-[hsl(var(--vermillion))] hover:underline"
              >
                → Commission form
              </a>
            </div>
          </aside>

          <form
            onSubmit={onSubmit}
            data-testid={TID.contactForm}
            className="lg:col-span-8 space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Your name" required>
                <input
                  required
                  data-testid={TID.contactName}
                  value={form.name}
                  onChange={set("name")}
                  className="input"
                />
              </Field>
              <Field label="Email" required>
                <input
                  required
                  type="email"
                  data-testid={TID.contactEmail}
                  value={form.email}
                  onChange={set("email")}
                  className="input"
                />
              </Field>
            </div>

            <Field label="Inquiry type" required>
              <select
                data-testid={TID.contactType}
                value={form.inquiry_type}
                onChange={set("inquiry_type")}
                className="input"
              >
                {TYPES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Subject" required>
              <input
                required
                data-testid={TID.contactSubject}
                value={form.subject}
                onChange={set("subject")}
                className="input"
              />
            </Field>

            <Field label="Message" required>
              <textarea
                required
                rows={7}
                data-testid={TID.contactMessage}
                value={form.message}
                onChange={set("message")}
                className="input resize-y"
              />
            </Field>

            <button
              type="submit"
              disabled={busy}
              data-testid={TID.contactSubmit}
              className="inline-flex items-center gap-2 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] px-8 py-4 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--vermillion))] transition-colors disabled:opacity-50"
            >
              {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          padding: 12px 14px;
          background: hsl(var(--surface));
          border: 1px solid hsl(var(--line));
          border-radius: 2px;
          font-size: 15px;
          color: hsl(var(--ink));
          transition: border-color 200ms ease;
        }
        .input:focus {
          outline: none;
          border-color: hsl(var(--ink));
        }
      `}</style>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="catalog-label mb-2 block">
        {label}
        {required && <span className="text-[hsl(var(--vermillion))]"> *</span>}
      </span>
      {children}
    </label>
  );
}
